<template>
  <v-navigation-drawer
    :model-value="modelValue"
    location="right"
    temporary
    width="460"
    class="ncrm-property-layout-drawer"
    @update:model-value="onDrawerModelUpdate"
  >
    <div class="d-flex flex-column fill-height">
      <div class="d-flex align-center justify-space-between ga-2 pa-4">
        <div>
          <div class="text-h6">{{ title }}</div>
          <div v-if="subtitle" class="text-body-2 text-medium-emphasis mt-1">
            {{ subtitle }}
          </div>
        </div>
        <v-btn icon="mdi-close" variant="text" @click="closeDrawer" />
      </div>
      <v-divider />

      <div class="flex-grow-1 overflow-y-auto pa-4">
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />
        <template v-else>
          <div class="text-caption text-medium-emphasis mb-2">
            {{ $t("ncrm.contacts.details.propertyLayout.visibleTitle") }}
          </div>
          <v-alert
            v-if="!visibleItems.length"
            type="warning"
            density="comfortable"
            class="mb-3"
          >
            {{ $t("ncrm.contacts.details.propertyLayout.requiredWarning") }}
          </v-alert>
          <v-list
            v-else
            density="compact"
            lines="one"
            class="ncrm-property-layout-drawer__list pa-0"
          >
            <v-list-item
              v-for="(item, index) in visibleItems"
              :key="`visible-${item.key}`"
              class="ncrm-property-layout-drawer__item"
              :class="visibleDragItemClasses(item.key)"
              tabindex="0"
              aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
              :draggable="visibleItems.length > 1"
              @dragstart="onVisibleDragStart($event, item.key)"
              @dragover.prevent="onVisibleDragOver($event, item.key)"
              @dragenter.prevent="onVisibleDragOver($event, item.key)"
              @dragleave="onVisibleDragLeave($event, item.key)"
              @drop.prevent="onVisibleDrop($event, item.key)"
              @dragend="onVisibleDragEnd"
              @keydown="onVisibleItemKeydown($event, index)"
            >
              <template #prepend>
                <v-icon
                  icon="mdi-drag-vertical"
                  size="small"
                  class="ncrm-property-layout-drawer__drag-handle"
                />
              </template>
              <v-list-item-title>{{ item.label }}</v-list-item-title>
              <template #append>
                <div class="d-flex align-center ga-1">
                  <v-btn
                    icon="mdi-arrow-up"
                    size="x-small"
                    variant="text"
                    color="primary"
                    :disabled="index === 0"
                    :aria-label="$t('ncrm.contacts.details.propertyLayout.actions.moveUp')"
                    @click.stop="moveVisible(index, -1)"
                  />
                  <v-btn
                    icon="mdi-arrow-down"
                    size="x-small"
                    variant="text"
                    color="primary"
                    :disabled="index === visibleItems.length - 1"
                    :aria-label="$t('ncrm.contacts.details.propertyLayout.actions.moveDown')"
                    @click.stop="moveVisible(index, 1)"
                  />
                  <v-btn
                    icon="mdi-eye-off-outline"
                    size="x-small"
                    variant="text"
                    color="error"
                    :disabled="visibleItems.length <= 1"
                    :aria-label="$t('ncrm.contacts.details.propertyLayout.actions.hide')"
                    @click.stop="hideVisible(index)"
                  />
                </div>
              </template>
            </v-list-item>
          </v-list>

          <v-divider class="my-4" />

          <div class="text-caption text-medium-emphasis mb-2">
            {{ $t("ncrm.contacts.details.propertyLayout.hiddenTitle") }}
          </div>
          <v-list
            v-if="hiddenItems.length"
            density="compact"
            lines="one"
            class="ncrm-property-layout-drawer__list pa-0"
          >
            <v-list-item
              v-for="item in hiddenItems"
              :key="`hidden-${item.key}`"
              class="ncrm-property-layout-drawer__item"
            >
              <v-list-item-title>{{ item.label }}</v-list-item-title>
              <template #append>
                <v-btn
                  icon="mdi-plus"
                  size="x-small"
                  variant="text"
                  color="primary"
                  :aria-label="$t('ncrm.contacts.details.propertyLayout.actions.show')"
                  @click.stop="showHidden(item.key)"
                />
              </template>
            </v-list-item>
          </v-list>
          <div v-else class="text-body-2 text-medium-emphasis">
            {{ $t("ncrm.contacts.details.propertyLayout.emptyHidden") }}
          </div>
        </template>
      </div>

      <v-divider />
      <div class="d-flex justify-end ga-2 pa-4">
        <v-btn variant="text" @click="closeDrawer">
          {{ $t("app.actions.cancel") }}
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          :loading="saving"
          :disabled="loading || !canSave"
          @click="submitLayout"
        >
          {{ $t("app.actions.save") }}
        </v-btn>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
export default {
  name: "NcrmPropertyLayoutDrawer",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    subtitle: {
      type: String,
      default: "",
    },
    items: {
      type: Array,
      default: () => [],
    },
    visibleKeys: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      draftVisibleKeys: [],
      dragVisibleKey: "",
      dragOverVisibleKey: "",
      dragOverVisiblePosition: "before",
    };
  },
  computed: {
    normalizedItems() {
      const seen = new Set();
      const normalized = [];
      for (const entry of this.items || []) {
        const key = String(entry?.key || "").trim();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        normalized.push({
          key,
          label: String(entry?.label || "").trim() || key,
        });
      }
      return normalized;
    },
    itemMap() {
      return this.normalizedItems.reduce((acc, entry) => {
        acc[entry.key] = entry;
        return acc;
      }, {});
    },
    visibleItems() {
      return this.draftVisibleKeys
        .map((key) => this.itemMap[key])
        .filter(Boolean);
    },
    hiddenItems() {
      const visibleSet = new Set(this.draftVisibleKeys);
      return this.normalizedItems.filter((entry) => !visibleSet.has(entry.key));
    },
    canSave() {
      return this.draftVisibleKeys.length > 0;
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        if (value) this.resetDraftFromProps();
      },
    },
    items: {
      deep: true,
      handler() {
        if (!this.modelValue) return;
        this.resetDraftFromProps();
      },
    },
    visibleKeys: {
      deep: true,
      handler() {
        if (!this.modelValue) return;
        this.resetDraftFromProps();
      },
    },
  },
  methods: {
    resetDraftFromProps() {
      this.resetVisibleDragState();
      const availableSet = new Set(this.normalizedItems.map((entry) => entry.key));
      const requested = Array.isArray(this.visibleKeys) ? this.visibleKeys : [];
      const deduped = [];
      const seen = new Set();
      for (const key of requested) {
        const normalizedKey = String(key || "").trim();
        if (!normalizedKey || seen.has(normalizedKey) || !availableSet.has(normalizedKey)) continue;
        seen.add(normalizedKey);
        deduped.push(normalizedKey);
      }
      if (deduped.length) {
        this.draftVisibleKeys = deduped;
        return;
      }
      this.draftVisibleKeys = this.normalizedItems.map((entry) => entry.key);
    },
    onDrawerModelUpdate(value) {
      this.$emit("update:modelValue", Boolean(value));
    },
    closeDrawer() {
      this.$emit("update:modelValue", false);
    },
    resetVisibleDragState() {
      this.dragVisibleKey = "";
      this.dragOverVisibleKey = "";
      this.dragOverVisiblePosition = "before";
    },
    visibleDragItemClasses(key) {
      const normalizedKey = String(key || "").trim();
      if (!normalizedKey) return {};
      const isDragged = normalizedKey === this.dragVisibleKey;
      const isTarget = normalizedKey === this.dragOverVisibleKey && normalizedKey !== this.dragVisibleKey;
      return {
        "ncrm-property-layout-drawer__item--dragging": isDragged,
        "ncrm-property-layout-drawer__item--drag-target-before":
          isTarget && this.dragOverVisiblePosition === "before",
        "ncrm-property-layout-drawer__item--drag-target-after":
          isTarget && this.dragOverVisiblePosition === "after",
      };
    },
    resolveDragOverPosition(event) {
      if (!event?.currentTarget?.getBoundingClientRect) return "after";
      const rect = event.currentTarget.getBoundingClientRect();
      if (!rect || !Number.isFinite(rect.top) || !Number.isFinite(rect.height) || rect.height <= 0) {
        return "after";
      }
      const pointerY = Number(event.clientY);
      if (!Number.isFinite(pointerY)) return "after";
      const threshold = rect.top + rect.height / 2;
      return pointerY < threshold ? "before" : "after";
    },
    onVisibleDragStart(event, key) {
      if (this.draftVisibleKeys.length <= 1) return;
      const normalizedKey = String(key || "").trim();
      if (!normalizedKey || !this.draftVisibleKeys.includes(normalizedKey)) return;
      this.dragVisibleKey = normalizedKey;
      this.dragOverVisibleKey = normalizedKey;
      this.dragOverVisiblePosition = "after";

      try {
        if (event?.dataTransfer) {
          event.dataTransfer.effectAllowed = "move";
          event.dataTransfer.setData("text/plain", normalizedKey);
        }
      } catch (_) {
        // No-op: some browsers/tests restrict dataTransfer access.
      }
    },
    onVisibleDragOver(event, key) {
      if (!this.dragVisibleKey) return;
      const normalizedKey = String(key || "").trim();
      if (!normalizedKey || !this.draftVisibleKeys.includes(normalizedKey)) return;
      if (event?.dataTransfer) {
        event.dataTransfer.dropEffect = "move";
      }
      this.dragOverVisibleKey = normalizedKey;
      this.dragOverVisiblePosition = this.resolveDragOverPosition(event);
    },
    onVisibleDragLeave(event, key) {
      const normalizedKey = String(key || "").trim();
      if (!normalizedKey || normalizedKey !== this.dragOverVisibleKey) return;
      const currentTarget = event?.currentTarget;
      const relatedTarget = event?.relatedTarget;
      if (currentTarget && relatedTarget && typeof currentTarget.contains === "function") {
        if (currentTarget.contains(relatedTarget)) return;
      }
      if (normalizedKey === this.dragVisibleKey) return;
      this.dragOverVisibleKey = "";
    },
    reorderVisibleByKeys(draggedKey, targetKey, position = "after") {
      const normalizedDraggedKey = String(draggedKey || "").trim();
      const normalizedTargetKey = String(targetKey || "").trim();
      const normalizedPosition = position === "before" ? "before" : "after";
      if (!normalizedDraggedKey || !normalizedTargetKey) return;
      if (normalizedDraggedKey === normalizedTargetKey && normalizedPosition === "after") return;

      const sourceIndex = this.draftVisibleKeys.indexOf(normalizedDraggedKey);
      const targetIndex = this.draftVisibleKeys.indexOf(normalizedTargetKey);
      if (sourceIndex < 0 || targetIndex < 0) return;

      const next = this.draftVisibleKeys.slice();
      const [moved] = next.splice(sourceIndex, 1);
      let insertionIndex = targetIndex;
      if (sourceIndex < targetIndex) {
        insertionIndex -= 1;
      }
      if (normalizedPosition === "after") {
        insertionIndex += 1;
      }
      if (insertionIndex < 0) insertionIndex = 0;
      if (insertionIndex > next.length) insertionIndex = next.length;
      next.splice(insertionIndex, 0, moved);
      this.draftVisibleKeys = next;
    },
    onVisibleDrop(event, key) {
      if (!this.dragVisibleKey) {
        this.resetVisibleDragState();
        return;
      }
      const targetKey = String(key || "").trim();
      const position = this.resolveDragOverPosition(event) || this.dragOverVisiblePosition;
      this.reorderVisibleByKeys(this.dragVisibleKey, targetKey, position);
      this.resetVisibleDragState();
    },
    onVisibleDragEnd() {
      this.resetVisibleDragState();
    },
    onVisibleItemKeydown(event, index) {
      if (!event?.altKey) return;
      const key = String(event.key || "").trim();
      if (key === "ArrowUp") {
        if (typeof event.preventDefault === "function") event.preventDefault();
        if (typeof event.stopPropagation === "function") event.stopPropagation();
        this.moveVisible(index, -1);
        return;
      }
      if (key === "ArrowDown") {
        if (typeof event.preventDefault === "function") event.preventDefault();
        if (typeof event.stopPropagation === "function") event.stopPropagation();
        this.moveVisible(index, 1);
      }
    },
    moveVisible(index, delta) {
      const sourceIndex = Number(index);
      const targetIndex = sourceIndex + Number(delta || 0);
      if (
        sourceIndex < 0 ||
        sourceIndex >= this.draftVisibleKeys.length ||
        targetIndex < 0 ||
        targetIndex >= this.draftVisibleKeys.length
      ) {
        return;
      }
      const next = this.draftVisibleKeys.slice();
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, moved);
      this.draftVisibleKeys = next;
    },
    hideVisible(index) {
      if (this.draftVisibleKeys.length <= 1) return;
      const targetIndex = Number(index);
      if (targetIndex < 0 || targetIndex >= this.draftVisibleKeys.length) return;
      const next = this.draftVisibleKeys.slice();
      next.splice(targetIndex, 1);
      this.draftVisibleKeys = next;
    },
    showHidden(key) {
      const normalizedKey = String(key || "").trim();
      if (!normalizedKey || !this.itemMap[normalizedKey]) return;
      if (this.draftVisibleKeys.includes(normalizedKey)) return;
      this.draftVisibleKeys = [...this.draftVisibleKeys, normalizedKey];
    },
    submitLayout() {
      if (!this.canSave) return;
      this.$emit("save", this.draftVisibleKeys.slice());
    },
  },
};
</script>

<style scoped>
.ncrm-property-layout-drawer__list {
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
}

.ncrm-property-layout-drawer__item {
  border-bottom: 1px solid var(--ncrm-color-border-subtle);
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    opacity 0.15s ease;
}

.ncrm-property-layout-drawer__item:last-child {
  border-bottom: none;
}

.ncrm-property-layout-drawer__item:focus-visible {
  outline: 2px solid var(--ncrm-color-primary-focus);
  outline-offset: -2px;
}

.ncrm-property-layout-drawer__drag-handle {
  cursor: grab;
  color: rgba(var(--v-theme-on-surface), 0.56);
}

.ncrm-property-layout-drawer__item:active .ncrm-property-layout-drawer__drag-handle {
  cursor: grabbing;
}

.ncrm-property-layout-drawer__item--dragging {
  opacity: 0.65;
  background-color: var(--ncrm-color-drag-source-bg);
}

.ncrm-property-layout-drawer__item--drag-target-before {
  border-top: 2px solid var(--ncrm-color-drag-target-outline);
}

.ncrm-property-layout-drawer__item--drag-target-after {
  border-bottom: 2px solid var(--ncrm-color-drag-target-outline);
}
</style>
